// Shared style → SVG-attribute parsing, used by the text renderer (for -webkit-text-stroke)
// and by container shapes (background / border / radius / shadow).

import type { Style } from '../project/posterSchema'

export interface BorderParsed { width: number; color: string }

// Parse border shorthand "3px solid #1565c0" → { width, color }
export function parseBorder(borderStr: string | null | undefined): BorderParsed | null {
	if (!borderStr) return null
	const m = borderStr.match(/([\d.]+)px\s+\w+\s+(.+)/)
	return m ? { width: +m[1], color: m[2].trim() } : null
}

export interface ShapeAttrs {
	fill:         string
	stroke?:      string
	strokeWidth?: number
	rx?:          number | string
	filter?:      string
}

// ── Corner radii ────────────────────────────────────────────────────────────
//
// Each corner carries its own radius under its own camelCase style key (so it binds to a
// theme variable like any other style key). A corner without a key inherits the legacy
// single-value radius (style.rx, or a numeric borderRadius), which keeps existing designs —
// and the JSON schema's borderRadius — rendering exactly as before.

export interface Corners { tl: number; tr: number; br: number; bl: number }

export interface CornerSpec {
	key:    string
	label:  string
	corner: keyof Corners
}

export const cornerSpecs: CornerSpec[] = [
	{ key: 'rxTL', label: 'Top left',     corner: 'tl' },
	{ key: 'rxTR', label: 'Top right',    corner: 'tr' },
	{ key: 'rxBR', label: 'Bottom right', corner: 'br' },
	{ key: 'rxBL', label: 'Bottom left',  corner: 'bl' },
]

// Percentage radii ("50%" → circle, "30%") have no per-corner equivalent here and are left
// to the rect/circle path, so they read as no corner radius at all.
function radiusValue(raw: Style[string] | undefined, fallback: number): number {
	if (raw == null) return fallback
	if (typeof raw === 'string' && raw.includes('%')) return 0
	const num = typeof raw === 'number' ? raw : parseFloat(raw as string)
	return Number.isFinite(num) && num > 0 ? num : 0
}

export function extractCorners(style: Style | undefined): Corners {
	const base = radiusValue(style?.rx ?? style?.borderRadius, 0)
	return {
		tl: radiusValue(style?.rxTL, base),
		tr: radiusValue(style?.rxTR, base),
		br: radiusValue(style?.rxBR, base),
		bl: radiusValue(style?.rxBL, base),
	}
}

export function cornersUniform(c: Corners): boolean {
	return c.tl === c.tr && c.tr === c.br && c.br === c.bl
}

function sideRatio(side: number, sum: number): number {
	return sum > 0 ? side / sum : 1
}

// Outline of a rectangle with per-corner radii. Radii that would overlap along a side are all
// scaled down by the same factor, as CSS border-radius does.
export function roundedRectPath(width: number, height: number, corners: Corners): string {
	const w = Math.max(0, width)
	const h = Math.max(0, height)
	const f = Math.min(1,
		sideRatio(w, corners.tl + corners.tr),
		sideRatio(w, corners.bl + corners.br),
		sideRatio(h, corners.tl + corners.bl),
		sideRatio(h, corners.tr + corners.br),
	)
	const tl = corners.tl * f
	const tr = corners.tr * f
	const br = corners.br * f
	const bl = corners.bl * f

	return [
		`M ${tl} 0`,
		`H ${w - tr}`,
		tr ? `A ${tr} ${tr} 0 0 1 ${w} ${tr}` : '',
		`V ${h - br}`,
		br ? `A ${br} ${br} 0 0 1 ${w - br} ${h}` : '',
		`H ${bl}`,
		bl ? `A ${bl} ${bl} 0 0 1 0 ${h - bl}` : '',
		`V ${tl}`,
		tl ? `A ${tl} ${tl} 0 0 1 ${tl} 0` : '',
		'Z',
	].filter(part => part !== '').join(' ')
}

// ── Image filters ───────────────────────────────────────────────────────────
//
// Each image filter is a numeric CSS filter function stored under its own camelCase
// key on the image's style (so it binds to a theme variable like any other style key).
// `def` is the no-op value that is omitted from the rendered filter string.

export interface ImageFilterSpec {
	key:   string
	label: string
	fn:    string
	unit:  string
	def:   number
	min:   number
	max:   number
	step:  number
}

export const imageFilterSpecs: ImageFilterSpec[] = [
	{ key: 'brightness', label: 'Brightness', fn: 'brightness', unit: '',   def: 1, min: 0, max: 3,  step: 0.05 },
	{ key: 'saturate',   label: 'Saturate',   fn: 'saturate',   unit: '',   def: 1, min: 0, max: 3,  step: 0.05 },
	{ key: 'blur',       label: 'Blur',       fn: 'blur',       unit: 'px', def: 0, min: 0, max: 20, step: 0.5  },
	{ key: 'grayscale',  label: 'Grayscale',  fn: 'grayscale',  unit: '',   def: 0, min: 0, max: 1,  step: 0.05 },
	{ key: 'sepia',      label: 'Sepia',      fn: 'sepia',      unit: '',   def: 0, min: 0, max: 1,  step: 0.05 },
]

// Builds the CSS `filter` string for an image from its (effective) style, skipping any
// filter left at its no-op default. Returns undefined when nothing applies so callers can
// leave the filter off entirely.
export function imageFilterCss(style: Style | undefined): string | undefined {
	if (!style) return undefined
	const parts: string[] = []
	for (const spec of imageFilterSpecs) {
		const raw = style[spec.key]
		if (raw == null) continue
		const num = typeof raw === 'number' ? raw : parseFloat(raw as string)
		if (Number.isNaN(num) || num === spec.def) continue
		parts.push(`${spec.fn}(${num}${spec.unit})`)
	}
	return parts.length ? parts.join(' ') : undefined
}

export function extractShapeAttrs(style: Style | undefined): ShapeAttrs {
	if (!style) return { fill: 'transparent' }
	const bg     = (style.background ?? style.backgroundColor) as string | undefined
	const bgFill = bg && !bg.includes('gradient') ? bg : undefined
	const border = parseBorder(style.border as string | undefined)
	const br     = style.borderRadius
	let rx: number | string | undefined
	if (br === '50%') rx = '50%'
	else if (typeof br === 'number') rx = br
	else if (typeof br === 'string') rx = br
	if (style.rx != null) rx = style.rx as number | string
	const shadow = style.boxShadow as string | undefined
	const filter = shadow ? `drop-shadow(${shadow})` : undefined
	return {
		fill:        (style.fill ?? bgFill ?? 'transparent') as string,
		stroke:      (style.stroke ?? border?.color ?? style.borderColor) as string | undefined,
		strokeWidth: (style.strokeWidth ?? border?.width) as number | undefined,
		rx,
		filter,
	}
}
